import { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { getPessoas } from "@/services/companyService"
import { MembroDataType } from "@/types/membros"
import { Card, CardHeader, CardTitle } from "@/components/ui/card"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Skeleton } from "@/components/ui/skeleton"
import { useLoading } from "@/hooks/useLoading"
import { useAuth } from "@/modules/Auth/context/AuthContext"
import { motion, Variants } from "framer-motion"
import { ArrowRight } from "lucide-react"
import { SubscriptionsByMemberFilter } from "./SubscriptionsByMemberFilter"

export function SubscriptionsByMember() {
  const { user } = useAuth()
  const [members, setMembers] = useState<MembroDataType[]>([])
  const [filteredMembers, setFilteredMembers] = useState<MembroDataType[]>([])
  const [loading, setLoading] = useState(true)
  const { showLoading, hideLoading } = useLoading()
  const [error, setError] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState("")
  const [inscriptionFilter, setInscriptionFilter] = useState("pendentes")

  useEffect(() => {
    showLoading()
    const loadMembers = async () => {
      try {
        setLoading(true)
        const response = await getPessoas(user.empId, {})
        setMembers(response.data.data)
        setFilteredMembers(response.data.data)
      } catch (err) {
        setError("Failed to fetch members")
        console.error(err)
      } finally {
        setLoading(false)
        hideLoading()
      }
    }

    loadMembers()
  }, [])

  useEffect(() => {
    const lowercasedQuery = searchQuery.toLowerCase()
    const filtered = members
      .filter((member) => {
        return (
          member.pes_nome.toLowerCase().includes(lowercasedQuery) ||
          member.pes_email.toLowerCase().includes(lowercasedQuery)
        )
      })
      .filter((member) => {
        if (inscriptionFilter === "pendentes") return member.inscricao_pendente

        return member
      })
    setFilteredMembers(filtered)
  }, [searchQuery, members, inscriptionFilter])

  if (error) {
    return (
      <div className="text-red-500">
        <h1>Inscrições por Colaborador</h1>
        <p>{error}</p>
      </div>
    )
  }

  const itemVariants: Variants = {
    hidden: { opacity: 0, y: 15 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: [0.215, 0.61, 0.355, 1] } },
  }

  return (
    <div>
      <h1 className="mb-4 text-2xl font-bold">Inscrições por Colaborador</h1>

      <div className="mb-4">
        <SubscriptionsByMemberFilter
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          inscriptionFilter={inscriptionFilter}
          onInscriptionFilterChange={setInscriptionFilter}
        />
      </div>

      {loading ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
          {Array.from({ length: 8 }).map((_, index) => (
            <Card key={index}>
              <CardHeader className="flex flex-row items-center gap-4">
                <Skeleton className="size-12 rounded-full" />
                <div className="space-y-2">
                  <Skeleton className="h-4 w-[150px]" />
                  <Skeleton className="h-4 w-[100px]" />
                </div>
              </CardHeader>
            </Card>
          ))}
        </div>
      ) : (
        <motion.div
          initial="hidden"
          animate="visible"
          variants={{ visible: { transition: { staggerChildren: 0.05 } } }}
          className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-3"
        >
          {filteredMembers.map((member) => (
            <motion.div
              variants={itemVariants}
              layout
            >
              <Link
                to={`/subscriptions/member/${member.pes_id}/${member.pes_nome}`}
                key={member.pes_id}
              >
                <Card className="group flex h-full cursor-pointer items-center justify-between overflow-hidden p-4 transition-all duration-500 hover:scale-105 hover:border-orange-600 hover:shadow-lg">
                  <CardHeader className="flex flex-row items-center gap-4 overflow-hidden p-0">
                    <Avatar className="size-12">
                      <AvatarImage
                        src={member.pes_foto_url}
                        alt={member.pes_nome}
                      />
                      <AvatarFallback>
                        {member.pes_nome
                          .split(" ")
                          .map((n) => n[0])
                          .join("")}
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 flex-1">
                      <CardTitle className="text-lg">{member.pes_nome}</CardTitle>
                      <p className="truncate text-sm text-gray-500">{member.pes_email}</p>
                    </div>
                  </CardHeader>
                  <ArrowRight
                    size={14}
                    className="-translate-x-2 text-primary opacity-0 transition-all group-hover:translate-x-0 group-hover:opacity-100"
                  />
                </Card>
              </Link>
            </motion.div>
          ))}
        </motion.div>
      )}
    </div>
  )
}
